import { MenuProps, Dropdown, Space } from 'antd';
import { useEffect, useState } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useThemeContext } from '../../contexts/ThemeContext';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { RoleName } from '../../enums/RoleNameEnm';
import { usePermissions } from '../../hooks/usePermissions';
import PrimaryButton from '../buttons/PrimaryButton';
import { Translations } from '../Translations/Translations';
import { IoSettingsOutline } from 'react-icons/io5';
import InstallButton from '../installButton/InstallButton';
import { useIsOnlineContext } from '../../contexts/IsOnlineContext';
import { referenceDataSyncService } from '../../services/referenceDataSyncService';
import dayjs from 'dayjs';

export interface NotificationInterface {
  id: number;
  content: string;
  template: string;
  viewed: boolean;
  createTime: number;
}

const LayoutHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOnline } = useIsOnlineContext();
  const { activeThemeConfig } = useThemeContext();
  const { t } = useTranslation(['topNavigation']);
  const [mainMenu, setMainMenu] = useState<string[]>();
  const [myMenu, setMyMenu] = useState<string[]>();

  const [userName, setUserName] = useState<string | null>(null);
  const { hasPermission, hasRoles } = usePermissions();
  const canAccessAccessManagement =
    hasPermission([PermissionEnum.USER_PERMISSION, PermissionEnum.VIEW_USER_PERMISSION]) &&
    hasPermission([PermissionEnum.VIEW_USER_ROLE, PermissionEnum.USER_ROLE]);
  const canAccessAdministration =
    hasRoles([RoleName.ADMIN, RoleName.SYSTEM_ADMIN]) &&
    hasPermission([
      PermissionEnum.MANAGE_USER,
      PermissionEnum.VIEW_USER_ROLE,
      PermissionEnum.CREATE_PRODUCTS,
      PermissionEnum.VIEW_PRODUCTS,
      PermissionEnum.CREATE_PRESENTATION,
      PermissionEnum.VIEW_PRESENTATION,
      PermissionEnum.UPDATE_PRESENTATION,
      PermissionEnum.DELETE_PRESENTATION,
      PermissionEnum.VIEW_PRODUCT_CATEGORIES,
      PermissionEnum.MANAGE_PRODUCT_CATEGORIES,
      PermissionEnum.MANAGE_BASKETS,
      PermissionEnum.VIEW_BASKETS,
      PermissionEnum.VIEW_CONFIG_ALERTS,
      PermissionEnum.CONFIG_ALERTS,
      PermissionEnum.MANAGE_NEWS,
      PermissionEnum.MANAGE_NEWSLETTERS,
    ]);

  const [dbLastSyncTime, setDBLastSyncTime] = useState<number>();
  const [lastSyncStatus, setLastSyncStatus] = useState<string | undefined>();

  useEffect(() => {
    const storedName = localStorage.getItem('name');
    if (storedName) {
      setUserName(storedName);
    }
  }, []);

  const mobileItemsDefault: MenuProps['items'] = [
    {
      label: (
        <div className="mb-2">
          <Translations />
        </div>
      ),
      key: '0',
    },
    {
      label: (
        <>
          {localStorage.getItem('name') ? (
            <>
              <div className="flex w-full flex-row items-center gap-2">
                <Icon.Person className="rounded-full bg-gradient-to-t from-secondary to-primary p-2 text-3xl text-white" />{' '}
                {localStorage.getItem('name')}
              </div>
            </>
          ) : isOnline ? (
            <>
              <div className="flex w-full flex-col items-stretch">
                <PrimaryButton
                  handleOnClick={() => {
                    navigate('/signIn');
                  }}
                >
                  {t('signin')}
                </PrimaryButton>
              </div>
            </>
          ) : null}
        </>
      ),
      key: '1',
    },
    {
      type: 'divider',
    },
    {
      label: (
        <NavLink
          to="/"
          className={({ isActive }) =>
            isActive
              ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
              : 'text-font-color hover:!text-primary-font-color/50'
          }
        >
          {t('home')}
        </NavLink>
      ),
      key: '2',
    },
    {
      label: (
        <NavLink
          to="/citizenPortal"
          className={({ isActive }) =>
            isActive
              ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
              : 'text-font-color hover:!text-primary-font-color/50'
          }
        >
          {t('citizenPortal')}
        </NavLink>
      ),
      key: '3',
    },
  ];

  const [mobileItems, setMobileItems] = useState(mobileItemsDefault);

  const currentPage = location.pathname.replace(/^\/|\/$/g, '');

  useEffect(() => {
    if (currentPage.includes('dashboard')) {
      setMyMenu(['dashboard']);
      setMainMenu(['dashboard']);
    } else {
      setMyMenu([currentPage]);
      setMainMenu([currentPage]);
    }
  }, [currentPage]);

  useEffect(() => { }, [myMenu, mainMenu]);

  const userItems: MenuProps['items'] = [
    {
      label: (
        <NavLink
          to="/logout"
          className={({ isActive }) =>
            isActive
              ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
              : 'text-font-color hover:!text-primary-font-color/50'
          }
        >
          {t('logout')}
        </NavLink>
      ),
      key: '0',
    },
  ];

  const addRemoveNavForMobile = () => {
    let navWithPermission: MenuProps['items'] = mobileItemsDefault;
    const keysToRemove = ['4', '5', '6', '7', '8', '9'];
    navWithPermission = navWithPermission.filter(
      (item) => item?.key && !keysToRemove.includes(String(item.key))
    );
    if (
      hasPermission([
        PermissionEnum.RUN_INSPECTIONS,
        PermissionEnum.CREATE_INSPECTIONS,
        PermissionEnum.UPDATE_INSPECTIONS,
        PermissionEnum.VIEW_INSPECTIONS,
        PermissionEnum.DELETE_INSPECTIONS,
      ])
    ) {
      navWithPermission.push({
        label: (
          <NavLink
            to="/inspector"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('inspector')}
          </NavLink>
        ),
        key: '4',
      });
    }

    if (hasPermission(PermissionEnum.VIEW_ANALYTICS)) {
      navWithPermission.push({
        label: (
          <NavLink
            to="/analysis"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('analysisReport')}
          </NavLink>
        ),
        key: '5',
      });
    }

    if (canAccessAdministration) {
      navWithPermission.push({
        label: (
          <NavLink
            to="/administration"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('adminstration')}
          </NavLink>
        ),
        key: '6',
      });
    }

    navWithPermission.push(
      {
        label: (
          <NavLink
            to="/aboutUs"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('aboutUs')}
          </NavLink>
        ),
        key: '7',
      },
      {
        label: (
          <NavLink
            to="/contactUs"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('contactUs')}
          </NavLink>
        ),
        key: '8',
      },
      (function () {
        if (localStorage.getItem('name')) {
          return {
            label: (
              <NavLink to="/logout" className="flex w-full items-stretch">
                <PrimaryButton handleOnClick={() => { }} width="100%">
                  {t('logout')}
                </PrimaryButton>
              </NavLink>
            ),
            key: '9',
          };
        }

        return null;
      })()
    );

    setMobileItems(navWithPermission);
  };

  useEffect(() => {
    // if(window.innerWidth<1025){
    addRemoveNavForMobile();
    // }
  }, [t]);

  useEffect(() => {
    const getLastSyncTimeWrapper = async () => {
      const { lastSyncTime, syncStatus } = await referenceDataSyncService.getLastSyncTime();
      setDBLastSyncTime(lastSyncTime);
      setLastSyncStatus(syncStatus);
    };

    getLastSyncTimeWrapper();

    const interval = setInterval(getLastSyncTimeWrapper, 30 * 1000);
    window.addEventListener('referenceDataSynced', getLastSyncTimeWrapper);

    return () => {
      clearInterval(interval);
      window.removeEventListener('referenceDataSynced', getLastSyncTimeWrapper);
    };
  }, []);

  return (
    <>
      <nav className="mx-2 flex flex-row items-center justify-between lg:mx-[35px]">
        {/* desktop header */}

        <NavLink
          to="/"
          className="!mb-1 mt-2 w-[166px] min-[1024px]:max-[1230px]:!h-[50px] min-[1024px]:max-[1230px]:!w-[100px]"
        >
          <img src={activeThemeConfig.LOGO_WITH_NAME} alt="" />
        </NavLink>

        <div className="hidden gap-4 text-nowrap text-base lg:!flex lg:!flex-row lg:!items-center min-[1024px]:max-[1230px]:!text-sm">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('home')}
          </NavLink>
          <NavLink
            to="/citizenPortal"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('citizenPortal')}
          </NavLink>
          {hasPermission([
            PermissionEnum.RUN_INSPECTIONS,
            PermissionEnum.CREATE_INSPECTIONS,
            PermissionEnum.UPDATE_INSPECTIONS,
            PermissionEnum.VIEW_INSPECTIONS,
            PermissionEnum.DELETE_INSPECTIONS,
          ]) && (
              <NavLink
                to="/inspector"
                className={({ isActive }) =>
                  isActive
                    ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                    : 'text-font-color hover:!text-primary-font-color/50'
                }
              >
                {t('inspector')}
              </NavLink>
            )}
          {hasPermission(PermissionEnum.VIEW_ANALYTICS) && (
            <NavLink
              to="/analysis"
              className={({ isActive }) =>
                isActive
                  ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                  : 'text-font-color hover:!text-primary-font-color/50'
              }
            >
              {t('analysisReport')}
            </NavLink>
          )}
          {canAccessAdministration && (
            <NavLink
              to="/administration"
              className={({ isActive }) =>
                isActive
                  ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                  : 'text-font-color hover:!text-primary-font-color/50'
              }
            >
              {t('adminstration')}
            </NavLink>
          )}
          <NavLink
            to="/aboutUs"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('aboutUs')}
          </NavLink>
          <NavLink
            to="/contactUs"
            className={({ isActive }) =>
              isActive
                ? 'font-bold !text-primary-font-color transition-colors ease-in hover:!text-primary-font-color/70'
                : 'text-font-color hover:!text-primary-font-color/50'
            }
          >
            {t('contactUs')}
          </NavLink>
        </div>
        <InstallButton />

        <div className="hidden gap-4 lg:!flex lg:!flex-row lg:!items-center min-[1024px]:max-[1230px]:!gap-2">
          <Translations />
          {userName ? (
            <>
              {isOnline ? (
                <Dropdown menu={{ items: userItems }} placement="bottomLeft">
                  <div className="cursor-pointer rounded-full bg-gradient-to-t from-secondary to-primary p-3 text-white min-[1024px]:max-[1230px]:!p-2">
                    <Icon.Person />
                  </div>
                </Dropdown>
              ) : (
                <div className="cursor-pointer rounded-full bg-gradient-to-t from-secondary to-primary p-3 text-white min-[1024px]:max-[1230px]:!p-2">
                  <Icon.Person />
                </div>
              )}
              {canAccessAccessManagement && (
                <div
                  className="cursor-pointer rounded-full border border-default p-3 text-default min-[1024px]:max-[1230px]:!p-2"
                  onClick={() => {
                    navigate('/settings/accessManagement');
                  }}
                >
                  <IoSettingsOutline />
                </div>
              )}
            </>
          ) : isOnline ? (
            <PrimaryButton
              handleOnClick={() => {
                navigate('/signIn');
              }}
            >
              {t('signin')}
            </PrimaryButton>
          ) : null}
        </div>

        <div className="!mr-6 mt-3 block lg:hidden">
          <Dropdown
            menu={{ items: mobileItems }}
            dropdownRender={(menu) => <div className="[&>ul]:!py-4">{menu}</div>}
            trigger={['click', 'hover']}
          >
            <a onClick={(e) => e.preventDefault()}>
              <Space>
                <Icon.List className="text-3xl" />
              </Space>
            </a>
          </Dropdown>
        </div>
      </nav>

      <div className="mt-1 flex h-fit flex-row flex-wrap justify-between bg-card-bg-color px-2 py-0.5 text-sm font-normal text-black/60 lg:px-[48px]">
        <>
          {isOnline ? (
            <div className="flex flex-row items-center gap-3">
              {t('online')}
              <span className="relative flex size-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-green opacity-75"></span>
                <span className="relative inline-flex size-3 rounded-full bg-primary-green"></span>
              </span>
            </div>
          ) : (
            <div className="flex flex-row items-center gap-3">
              {t('offline')}
              <span className="relative flex size-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-red opacity-75"></span>
                <span className="relative inline-flex size-3 rounded-full bg-primary-red"></span>
              </span>
            </div>
          )}

          {dbLastSyncTime !== undefined &&
            dbLastSyncTime > 0 &&
            (lastSyncStatus === 'synced' || lastSyncStatus === 'pending') && (
              <span>
                {t('lastSyncTime')}: {dayjs(dbLastSyncTime).format('YYYY-MM-DD hh:mm A')}
              </span>
            )}
        </>
      </div>
    </>
  );
};

export default LayoutHeader;
